<template>
  <div class="ve-split">
    <!-- Left: list of selected terrains -->
    <div class="ve-list-panel">
      <div class="ve-list-header">
        <span class="ve-list-title">{{ selectedTerrainIds.length }} terrain{{ selectedTerrainIds.length === 1 ? '' : 's' }}</span>
        <button type="button" class="ve-add-btn" :class="{ 've-add-btn--active': mode === 'add' }" @click="startAdd">+ Add</button>
      </div>
      <div class="ve-list-body">
        <div v-if="selectedTerrainIds.length === 0" class="ve-list-empty">No terrain yet</div>
        <div
          v-for="(skinId, idx) in selectedTerrainIds"
          :key="skinId"
          class="ve-list-item"
          :class="{ 've-list-item--selected': mode === 'edit' && selectedIdx === idx }"
          @click="selectItem(idx)"
        >
          <img v-if="terrainSkinById(skinId).thumbnailUrl" :src="terrainSkinById(skinId).thumbnailUrl" class="ve-list-thumb" />
          <span class="ve-list-name">{{ (terrainDetails[skinId] || {}).name || terrainSkinById(skinId).name || skinId }}</span>
          <button type="button" class="ve-list-remove" title="Remove" @click.stop="removeTerrain(skinId)">✕</button>
        </div>
      </div>
    </div>

    <!-- Right: SkinGrid (add) or edit fields (edit) -->
    <div class="ve-detail-panel">
      <!-- ADD MODE -->
      <template v-if="mode === 'add'">
        <p class="ve-hint">Click a skin to add it. Click again to remove it.</p>
        <div v-if="terrainsLoading" class="field-desc">Loading terrain skins…</div>
        <div v-else-if="terrainsError" class="field-error">{{ terrainsError }}</div>
        <SkinGrid
          v-else
          :skins="terrainSkins"
          :selection-mode="true"
          :selected-ids="selectedTerrainIds"
          :show-info="false"
          @toggle="toggleTerrain"
        />
      </template>

      <!-- EDIT MODE -->
      <template v-else-if="mode === 'edit' && selectedSkinId">
        <div class="ve-edit-form">
          <div class="ve-edit-row-head">
            <img
              v-if="terrainSkinById(selectedSkinId).thumbnailUrl"
              :src="terrainSkinById(selectedSkinId).thumbnailUrl"
              class="ve-edit-thumb"
            />
            <div>
              <div class="ve-edit-skin-name">{{ terrainSkinById(selectedSkinId).name }}</div>
              <div class="ve-edit-hint">Terrain {{ selectedIdx + 1 }} of {{ selectedTerrainIds.length }}</div>
            </div>
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Name</label>
            <input
              :value="(terrainDetails[selectedSkinId] || {}).name"
              type="text"
              class="field-input"
              @input="onFieldInput(selectedSkinId, 'name', $event.target.value)"
            />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Width (in)</label>
            <input
              :value="(terrainDetails[selectedSkinId] || {}).widthIn"
              type="number" min="0" step="0.5"
              class="field-input"
              @input="onFieldInput(selectedSkinId, 'widthIn', parseFloat($event.target.value) || 0)"
            />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Length (in)</label>
            <input
              :value="(terrainDetails[selectedSkinId] || {}).lengthIn"
              type="number" min="0" step="0.5"
              class="field-input"
              @input="onFieldInput(selectedSkinId, 'lengthIn', parseFloat($event.target.value) || 0)"
            />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Shape</label>
            <div class="terrain-shape-toggle" style="max-width:200px">
              <button
                type="button"
                class="terrain-shape-btn"
                :class="{ active: (terrainDetails[selectedSkinId] || {}).shape === 0 }"
                @click="onFieldInput(selectedSkinId, 'shape', 0)"
              >Square</button>
              <button
                type="button"
                class="terrain-shape-btn"
                :class="{ active: (terrainDetails[selectedSkinId] || {}).shape === 1 }"
                @click="onFieldInput(selectedSkinId, 'shape', 1)"
              >Round</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SkinGrid from '@/components/SkinGrid.vue';

const IN_TO_CM = 2.54;

export default {
  name: 'TerrainEditor',
  components: { SkinGrid },

  props: {
    value: { type: Array, default: () => [] },
  },

  data() {
    return {
      terrainSkins: [],
      terrainsLoading: false,
      terrainsError: '',
      selectedTerrainIds: [],
      terrainDetails: {},
      mode: 'add',
      selectedIdx: null,
    };
  },

  computed: {
    selectedSkinId() {
      if (this.selectedIdx === null) return null;
      return this.selectedTerrainIds[this.selectedIdx] || null;
    },
  },

  async mounted() {
    await this.fetchTerrainSkins();
  },

  watch: {
    value(newVal) {
      if (this.terrainSkins.length > 0) {
        this.initFromValue(newVal);
      }
    },
  },

  methods: {
    startAdd() {
      this.mode = 'add';
      this.selectedIdx = null;
    },

    selectItem(idx) {
      this.mode = 'edit';
      this.selectedIdx = idx;
    },

    async fetchTerrainSkins() {
      this.terrainsLoading = true;
      this.terrainsError = '';
      try {
        const res = await fetch(
          '/GetCatalogueSkins?viewMode=public&search=terrain&perPage=200&page=1&sortOrder=newest',
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        this.terrainSkins = data.result.skins;
        if (this.value && this.value.length > 0) {
          this.initFromValue(this.value);
        }
      } catch (err) {
        this.terrainsError = 'Could not load terrain skins: ' + err.message;
      } finally {
        this.terrainsLoading = false;
      }
    },

    initFromValue(terrains) {
      const matched = this.terrainSkins.filter(s =>
        (terrains || []).some(t =>
          t.SkinId != null ? String(t.SkinId) === String(s.id) : t.Name === s.name
        )
      );
      this.selectedTerrainIds = matched.map(s => String(s.id));
      for (const s of matched) {
        const stored = (terrains || []).find(t =>
          t.SkinId != null ? String(t.SkinId) === String(s.id) : t.Name === s.name
        );
        if (stored) {
          this.$set(this.terrainDetails, String(s.id), {
            name: stored.Name,
            widthIn: stored.Width != null ? Math.round((stored.Width / IN_TO_CM) * 100) / 100 : 4,
            lengthIn: stored.Length != null ? Math.round((stored.Length / IN_TO_CM) * 100) / 100 : 4,
            shape: stored.Shape ?? 0,
          });
        }
      }
    },

    terrainSkinById(skinId) {
      return this.terrainSkins.find(s => String(s.id) === skinId) || {};
    },

    toggleTerrain(skin) {
      const id = String(skin.id);
      const idx = this.selectedTerrainIds.indexOf(id);
      if (idx === -1) {
        this.selectedTerrainIds.push(id);
        if (!this.terrainDetails[id]) {
          this.$set(this.terrainDetails, id, { name: skin.name, widthIn: 4, lengthIn: 4, shape: 0 });
        }
        this.mode = 'edit';
        this.selectedIdx = this.selectedTerrainIds.length - 1;
      } else {
        if (this.mode === 'edit' && this.selectedTerrainIds[this.selectedIdx] === id) {
          this.mode = 'add';
          this.selectedIdx = null;
        }
        this.selectedTerrainIds.splice(idx, 1);
      }
      this.$emit('input', this.buildTerrainJson());
    },

    removeTerrain(skinId) {
      const idx = this.selectedTerrainIds.indexOf(skinId);
      if (idx >= 0) {
        if (this.mode === 'edit' && this.selectedTerrainIds[this.selectedIdx] === skinId) {
          this.mode = 'add';
          this.selectedIdx = null;
        }
        this.selectedTerrainIds.splice(idx, 1);
      }
      this.$emit('input', this.buildTerrainJson());
    },

    onFieldInput(skinId, field, value) {
      this.$set(this.terrainDetails, skinId, { ...this.terrainDetails[skinId], [field]: value });
      this.$emit('input', this.buildTerrainJson());
    },

    buildTerrainJson() {
      return this.terrainSkins
        .filter(s => this.selectedTerrainIds.includes(String(s.id)))
        .map((s, i) => {
          const d = this.terrainDetails[String(s.id)] || {};
          return {
            Id: `terrain_${i}`,
            SkinId: s.id,
            Name: d.name || s.name,
            Type: 'Terrain',
            Width: Math.round(((d.widthIn ?? 4) * IN_TO_CM) * 100) / 100,
            Length: Math.round(((d.lengthIn ?? 4) * IN_TO_CM) * 100) / 100,
            Shape: d.shape ?? 0,
          };
        });
    },
  },
};
</script>

<style>
.terrain-shape-toggle { display: flex; border: 1px solid #30363d; border-radius: 6px; overflow: hidden; margin-top: 0.1rem; }
.terrain-shape-btn { flex: 1; padding: 0.3rem 0.75rem; background: #0d1117; color: #8b949e; border: none; cursor: pointer; font-size: 0.82rem; transition: background 0.15s, color 0.15s; }
.terrain-shape-btn + .terrain-shape-btn { border-left: 1px solid #30363d; }
.terrain-shape-btn.active { background: #1f6feb; color: #fff; font-weight: 600; }
</style>
